"use strict";
/**
 * 自选状态管理 composable
 * 参考 quote-rank-v2 的 useSelectStatus.js 和 useWchNum.js 实现
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSelfSelected = useSelfSelected;
const vue_demi_1 = require("vue-demi");
const { useCommonApi, useUser, useBehaviour } = require('@fegobull/webapi').use;
// 自选股票相关 API
const request = useCommonApi('self-selected-stocks');
const selfSelectedButtonRequest = useCommonApi('self-selected-button');
// 登录状态
const { isLogin } = useUser();
// 行为跳转（用于登录跳转）
const { runBehaviour } = useBehaviour();
/**
 * 自选状态管理 composable
 * @param stockListRef 股票列表的响应式引用
 * @param showWchNum 是否展示并加载自选人数，默认 true
 * @returns 自选状态相关数据和方法
 */
function useSelfSelected(stockListRef, showWchNum = true) {
    // 自选状态映射：stockId -> boolean
    const selfSelectedMap = (0, vue_demi_1.reactive)({});
    // 自选人数映射：stockId -> number
    const wchNumMap = (0, vue_demi_1.reactive)({});
    /**
     * 获取单个股票的自选状态
     */
    const getSelectStatus = async (stock) => {
        var _a, _b;
        if (!isLogin) {
            return;
        }
        const { stockId } = stock;
        let res = false;
        try {
            const rsp = await request.get('status', { stockId });
            if (rsp.code === 10002) {
                res = false;
            }
            else {
                res = (_b = (_a = rsp === null || rsp === void 0 ? void 0 : rsp.data) === null || _a === void 0 ? void 0 : _a.result) !== null && _b !== void 0 ? _b : false;
            }
        }
        catch (e) {
            console.error('getSelectStatus error:', e);
            res = false;
        }
        (0, vue_demi_1.set)(selfSelectedMap, stockId, res);
    };
    /**
     * 获取单个股票的自选人数
     */
    const getWchNum = async (stock) => {
        var _a;
        const { stockId } = stock;
        try {
            const rsp = await request.get('wchNum', { stockId });
            const result = ((_a = rsp === null || rsp === void 0 ? void 0 : rsp.data) === null || _a === void 0 ? void 0 : _a.result) || 0;
            (0, vue_demi_1.set)(wchNumMap, stockId, result);
        }
        catch (e) {
            console.error('getWchNum error:', e);
            (0, vue_demi_1.set)(wchNumMap, stockId, 0);
        }
    };
    /**
     * 切换自选状态（添加/取消自选）
     * @param stockId 股票 ID
     * @param $event 点击事件（用于登录跳转）
     * @returns 操作是否成功
     */
    const toggleSelfSelected = async (stockId, $event) => {
        if (!isLogin) {
            // 未登录，跳转登录页
            runBehaviour(null, { type: 'login', withQuery: true }, $event);
            return false;
        }
        const currentStatus = selfSelectedMap[stockId] || false;
        try {
            await request.post(currentStatus ? 'cancelSubscribeStock' : 'subscribeStock', { stockId });
            // 更新自选状态
            (0, vue_demi_1.set)(selfSelectedMap, stockId, !currentStatus);
            // 更新自选人数
            if (currentStatus) {
                // 取消自选，人数减一，最小为 0
                const result = (wchNumMap[stockId] || 0) - 1;
                (0, vue_demi_1.set)(wchNumMap, stockId, result < 0 ? 0 : result);
            }
            else {
                // 添加自选，人数加一
                (0, vue_demi_1.set)(wchNumMap, stockId, (wchNumMap[stockId] || 0) + 1);
            }
            return true;
        }
        catch (e) {
            console.error('toggleSelfSelected error:', e);
            return false;
        }
    };
    /**
     * 一键添加全部到自选（保留实现，暂不使用）
     * @param $event 点击事件（用于登录跳转）
     */
    const addAllToSelfSelected = async ($event) => {
        if (!isLogin) {
            // 未登录，跳转登录页
            runBehaviour(null, { type: 'login', withQuery: true }, $event);
            return false;
        }
        const stockList = stockListRef.value;
        if (!stockList || stockList.length === 0) {
            return false;
        }
        const stockIdsArr = stockList.map((stock) => stock.stockId);
        const stockIds = stockIdsArr.join(',');
        try {
            await selfSelectedButtonRequest.post('add-wch', { stockIds });
            // 更新所有股票的自选状态
            stockIdsArr.forEach((stockId) => {
                const isSelected = selfSelectedMap[stockId];
                if (!isSelected) {
                    (0, vue_demi_1.set)(selfSelectedMap, stockId, true);
                    (0, vue_demi_1.set)(wchNumMap, stockId, (wchNumMap[stockId] || 0) + 1);
                }
            });
            return true;
        }
        catch (e) {
            console.error('addAllToSelfSelected error:', e);
            return false;
        }
    };
    /**
     * 初始化所有股票的自选状态
     */
    const initSelectStatus = async () => {
        const stockList = stockListRef.value;
        if (!stockList || stockList.length === 0) {
            return;
        }
        // stockList.forEach(async (stock) => getSelectStatus(stock));
        for (const stock of stockList) {
            await getSelectStatus(stock);
        }
    };
    /**
     * 初始化所有股票的自选人数（串行加载，避免瞬间大量请求）
     */
    const initWchNum = async () => {
        const stockList = stockListRef.value;
        if (!stockList || stockList.length === 0) {
            return;
        }
        // 串行加载，一个接一个
        for (const stock of stockList) {
            await getWchNum(stock);
        }
    };
    /**
     * 初始化自选数据（状态 + 人数）
     * 如果配置关闭自选人数展示，则只加载自选状态
     */
    const initSelfSelected = async () => {
        console.log('showWchNum', showWchNum);
        await initSelectStatus();
        // 只有在配置开启时才加载自选人数
        if (showWchNum) {
            await initWchNum();
        }
    };
    return {
        /** 自选状态映射 */
        selfSelectedMap,
        /** 自选人数映射 */
        wchNumMap,
        /** 切换自选状态 */
        toggleSelfSelected,
        /** 一键添加全部到自选 */
        addAllToSelfSelected,
        /** 初始化自选数据 */
        initSelfSelected,
        /** 初始化自选状态 */
        initSelectStatus,
        /** 初始化自选人数 */
        initWchNum
    };
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidXNlU2VsZlNlbGVjdGVkLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsidXNlU2VsZlNlbGVjdGVkLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiI7QUFBQTs7O0dBR0c7O0FBK0JILDBDQWtMQztBQTlNRCx1Q0FBeUM7QUFFekMsTUFBTSxFQUFFLFlBQVksRUFBRSxPQUFPLEVBQUUsWUFBWSxFQUFFLEdBQUcsT0FBTyxDQUFDLGtCQUFrQixDQUFDLENBQUMsR0FBRyxDQUFDO0FBRWhGLGFBQWE7QUFDYixNQUFNLE9BQU8sR0FBRyxZQUFZLENBQUMsc0JBQXNCLENBQUMsQ0FBQztBQUNyRCxNQUFNLHlCQUF5QixHQUFHLFlBQVksQ0FBQyxzQkFBc0IsQ0FBQyxDQUFDO0FBRXZFLE9BQU87QUFDUCxNQUFNLEVBQUUsT0FBTyxFQUFFLEdBQUcsT0FBTyxFQUFFLENBQUM7QUFFOUIsZUFBZTtBQUNmLE1BQU0sRUFBRSxZQUFZLEVBQUUsR0FBRyxZQUFZLEVBQUUsQ0FBQztBQVV4Qzs7Ozs7R0FLRztBQUNILFNBQWdCLGVBQWUsQ0FBQyxZQUErQixFQUFFLGFBQXNCLElBQUk7SUFDdkYsNEJBQTRCO0lBQzVCLE1BQU0sZUFBZSxHQUFHLElBQUEsbUJBQVEsRUFBMEIsRUFBRSxDQUFDLENBQUM7SUFFOUQsMkJBQTJCO0lBQzNCLE1BQU0sU0FBUyxHQUFHLElBQUEsbUJBQVEsRUFBeUIsRUFBRSxDQUFDLENBQUM7SUFFdkQ7O09BRUc7SUFDSCxNQUFNLGVBQWUsR0FBRyxLQUFLLEVBQUUsS0FBaUIsRUFBRSxFQUFFOztRQUNoRCxJQUFJLENBQUMsT0FBTyxFQUFFLENBQUM7WUFDWCxPQUFPO1FBQ1gsQ0FBQztRQUNELE1BQU0sRUFBRSxPQUFPLEVBQUUsR0FBRyxLQUFLLENBQUM7UUFDMUIsSUFBSSxHQUFHLEdBQUcsS0FBSyxDQUFDO1FBQ2hCLElBQUksQ0FBQztZQUNELE1BQU0sR0FBRyxHQUFHLE1BQU0sT0FBTyxDQUFDLEdBQUcsQ0FBQyxRQUFRLEVBQUUsRUFBRSxPQUFPLEVBQUUsQ0FBQyxDQUFDO1lBQ3JELElBQUksR0FBRyxDQUFDLElBQUksS0FBSyxLQUFLLEVBQUUsQ0FBQztnQkFDckIsR0FBRyxHQUFHLEtBQUssQ0FBQztZQUNoQixDQUFDO2lCQUFNLENBQUM7Z0JBQ0osR0FBRyxHQUFHLE1BQUEsTUFBQSxHQUFHLGFBQUgsR0FBRyx1QkFBSCxHQUFHLENBQUUsSUFBSSwwQ0FBRSxNQUFNLG1DQUFJLEtBQUssQ0FBQztZQUNyQyxDQUFDO1FBQ0wsQ0FBQztRQUFDLE9BQU8sQ0FBQyxFQUFFLENBQUM7WUFDVCxPQUFPLENBQUMsS0FBSyxDQUFDLHdCQUF3QixFQUFFLENBQUMsQ0FBQyxDQUFDO1lBQzNDLEdBQUcsR0FBRyxLQUFLLENBQUM7UUFDaEIsQ0FBQztRQUNELElBQUEsY0FBRyxFQUFDLGVBQWUsRUFBRSxPQUFPLEVBQUUsR0FBRyxDQUFDLENBQUM7SUFDdkMsQ0FBQyxDQUFDO0lBRUY7O09BRUc7SUFDSCxNQUFNLFNBQVMsR0FBRyxLQUFLLEVBQUUsS0FBaUIsRUFBRSxFQUFFOztRQUMxQyxNQUFNLEVBQUUsT0FBTyxFQUFFLEdBQUcsS0FBSyxDQUFDO1FBQzFCLElBQUksQ0FBQztZQUNELE1BQU0sR0FBRyxHQUFHLE1BQU0sT0FBTyxDQUFDLEdBQUcsQ0FBQyxRQUFRLEVBQUUsRUFBRSxPQUFPLEVBQUUsQ0FBQyxDQUFDO1lBQ3JELE1BQU0sTUFBTSxHQUFHLENBQUEsTUFBQSxHQUFHLGFBQUgsR0FBRyx1QkFBSCxHQUFHLENBQUUsSUFBSSwwQ0FBRSxNQUFNLEtBQUksQ0FBQyxDQUFDO1lBQ3RDLElBQUEsY0FBRyxFQUFDLFNBQVMsRUFBRSxPQUFPLEVBQUUsTUFBTSxDQUFDLENBQUM7UUFDcEMsQ0FBQztRQUFDLE9BQU8sQ0FBQyxFQUFFLENBQUM7WUFDVCxPQUFPLENBQUMsS0FBSyxDQUFDLGtCQUFrQixFQUFFLENBQUMsQ0FBQyxDQUFDO1lBQ3JDLElBQUEsY0FBRyxFQUFDLFNBQVMsRUFBRSxPQUFPLEVBQUUsQ0FBQyxDQUFDLENBQUM7UUFDL0IsQ0FBQztJQUNMLENBQUMsQ0FBQztJQUVGOzs7OztPQUtHO0lBQ0gsTUFBTSxrQkFBa0IsR0FBRyxLQUFLLEVBQUUsT0FBZSxFQUFFLE1BQWMsRUFBb0IsRUFBRTtRQUNuRixJQUFJLENBQUMsT0FBTyxFQUFFLENBQUM7WUFDWCxZQUFZO1lBQ1osWUFBWSxDQUFDLElBQUksRUFBRSxFQUFFLElBQUksRUFBRSxPQUFPLEVBQUUsU0FBUyxFQUFFLElBQUksRUFBRSxFQUFFLE1BQU0sQ0FBQyxDQUFDO1lBQy9ELE9BQU8sS0FBSyxDQUFDO1FBQ2pCLENBQUM7UUFFRCxNQUFNLGFBQWEsR0FBRyxlQUFlLENBQUMsT0FBTyxDQUFDLElBQUksS0FBSyxDQUFDO1FBRXhELElBQUksQ0FBQztZQUNELE1BQU0sT0FBTyxDQUFDLElBQUksQ0FBQyxhQUFhLENBQUMsQ0FBQyxDQUFDLHNCQUFzQixDQUFDLENBQUMsQ0FBQyxnQkFBZ0IsRUFBRSxFQUFFLE9BQU8sRUFBRSxDQUFDLENBQUM7WUFFM0YsU0FBUztZQUNULElBQUEsY0FBRyxFQUFDLGVBQWUsRUFBRSxPQUFPLEVBQUUsQ0FBQyxhQUFhLENBQUMsQ0FBQztZQUU5QyxTQUFTO1lBQ1QsSUFBSSxhQUFhLEVBQUUsQ0FBQztnQkFDaEIsa0JBQWtCO2dCQUNsQixNQUFNLE1BQU0sR0FBRyxDQUFDLFNBQVMsQ0FBQyxPQUFPLENBQUMsSUFBSSxDQUFDLENBQUMsR0FBRyxDQUFDLENBQUM7Z0JBQzdDLElBQUEsY0FBRyxFQUFDLFNBQVMsRUFBRSxPQUFPLEVBQUUsTUFBTSxHQUFHLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQyxNQUFNLENBQUMsQ0FBQztZQUNyRCxDQUFDO2lCQUFNLENBQUM7Z0JBQ0osWUFBWTtnQkFDWixJQUFBLGNBQUcsRUFBQyxTQUFTLEVBQUUsT0FBTyxFQUFFLENBQUMsU0FBUyxDQUFDLE9BQU8sQ0FBQyxJQUFJLENBQUMsQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDO1lBQzNELENBQUM7WUFFRCxPQUFPLElBQUksQ0FBQztRQUNoQixDQUFDO1FBQUMsT0FBTyxDQUFDLEVBQUUsQ0FBQztZQUNULE9BQU8sQ0FBQyxLQUFLLENBQUMsMkJBQTJCLEVBQUUsQ0FBQyxDQUFDLENBQUM7WUFDOUMsT0FBTyxLQUFLLENBQUM7UUFDakIsQ0FBQztJQUNMLENBQUMsQ0FBQztJQUVGOzs7T0FHRztJQUNILE1BQU0sb0JBQW9CLEdBQUcsS0FBSyxFQUFFLE1BQWMsRUFBb0IsRUFBRTtRQUNwRSxJQUFJLENBQUMsT0FBTyxFQUFFLENBQUM7WUFDWCxZQUFZO1lBQ1osWUFBWSxDQUFDLElBQUksRUFBRSxFQUFFLElBQUksRUFBRSxPQUFPLEVBQUUsU0FBUyxFQUFFLElBQUksRUFBRSxFQUFFLE1BQU0sQ0FBQyxDQUFDO1lBQy9ELE9BQU8sS0FBSyxDQUFDO1FBQ2pCLENBQUM7UUFFRCxNQUFNLFNBQVMsR0FBRyxZQUFZLENBQUMsS0FBSyxDQUFDO1FBQ3JDLElBQUksQ0FBQyxTQUFTLElBQUksU0FBUyxDQUFDLE1BQU0sS0FBSyxDQUFDLEVBQUUsQ0FBQztZQUN2QyxPQUFPLEtBQUssQ0FBQztRQUNqQixDQUFDO1FBRUQsTUFBTSxXQUFXLEdBQUcsU0FBUyxDQUFDLEdBQUcsQ0FBQyxDQUFDLEtBQUssRUFBRSxFQUFFLENBQUMsS0FBSyxDQUFDLE9BQU8sQ0FBQyxDQUFDO1FBQzVELE1BQU0sUUFBUSxHQUFHLFdBQVcsQ0FBQyxJQUFJLENBQUMsR0FBRyxDQUFDLENBQUM7UUFFdkMsSUFBSSxDQUFDO1lBQ0QsTUFBTSx5QkFBeUIsQ0FBQyxJQUFJLENBQUMsU0FBUyxFQUFFLEVBQUUsUUFBUSxFQUFFLENBQUMsQ0FBQztZQUU5RCxjQUFjO1lBQ2QsV0FBVyxDQUFDLE9BQU8sQ0FBQyxDQUFDLE9BQU8sRUFBRSxFQUFFO2dCQUM1QixNQUFNLFVBQVUsR0FBRyxlQUFlLENBQUMsT0FBTyxDQUFDLENBQUM7Z0JBQzVDLElBQUksQ0FBQyxVQUFVLEVBQUUsQ0FBQztvQkFDZCxJQUFBLGNBQUcsRUFBQyxlQUFlLEVBQUUsT0FBTyxFQUFFLElBQUksQ0FBQyxDQUFDO29CQUNwQyxJQUFBLGNBQUcsRUFBQyxTQUFTLEVBQUUsT0FBTyxFQUFFLENBQUMsU0FBUyxDQUFDLE9BQU8sQ0FBQyxJQUFJLENBQUMsQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDO2dCQUMzRCxDQUFDO1lBQ0wsQ0FBQyxDQUFDLENBQUM7WUFFSCxPQUFPLElBQUksQ0FBQztRQUNoQixDQUFDO1FBQUMsT0FBTyxDQUFDLEVBQUUsQ0FBQztZQUNULE9BQU8sQ0FBQyxLQUFLLENBQUMsNkJBQTZCLEVBQUUsQ0FBQyxDQUFDLENBQUM7WUFDaEQsT0FBTyxLQUFLLENBQUM7UUFDakIsQ0FBQztJQUNMLENBQUMsQ0FBQztJQUVGOztPQUVHO0lBQ0gsTUFBTSxnQkFBZ0IsR0FBRyxLQUFLLElBQUksRUFBRTtRQUNoQyxNQUFNLFNBQVMsR0FBRyxZQUFZLENBQUMsS0FBSyxDQUFDO1FBQ3JDLElBQUksQ0FBQyxTQUFTLElBQUksU0FBUyxDQUFDLE1BQU0sS0FBSyxDQUFDLEVBQUUsQ0FBQztZQUN2QyxPQUFPO1FBQ1gsQ0FBQztRQUNELDhEQUE4RDtRQUM5RCxLQUFLLE1BQU0sS0FBSyxJQUFJLFNBQVMsRUFBRSxDQUFDO1lBQzVCLE1BQU0sZUFBZSxDQUFDLEtBQUssQ0FBQyxDQUFDO1FBQ2pDLENBQUM7SUFDTCxDQUFDLENBQUM7SUFFRjs7T0FFRztJQUNILE1BQU0sVUFBVSxHQUFHLEtBQUssSUFBSSxFQUFFO1FBQzFCLE1BQU0sU0FBUyxHQUFHLFlBQVksQ0FBQyxLQUFLLENBQUM7UUFDckMsSUFBSSxDQUFDLFNBQVMsSUFBSSxTQUFTLENBQUMsTUFBTSxLQUFLLENBQUMsRUFBRSxDQUFDO1lBQ3ZDLE9BQU87UUFDWCxDQUFDO1FBQ0QsYUFBYTtRQUNiLEtBQUssTUFBTSxLQUFLLElBQUksU0FBUyxFQUFFLENBQUM7WUFDNUIsTUFBTSxTQUFTLENBQUMsS0FBSyxDQUFDLENBQUM7UUFDM0IsQ0FBQztJQUNMLENBQUMsQ0FBQztJQUVGOzs7T0FHRztJQUNILE1BQU0sZ0JBQWdCLEdBQUcsS0FBSyxJQUFJLEVBQUU7UUFDaEMsT0FBTyxDQUFDLEdBQUcsQ0FBQyxZQUFZLEVBQUUsVUFBVSxDQUFDLENBQUM7UUFDdEMsTUFBTSxnQkFBZ0IsRUFBRSxDQUFDO1FBQ3pCLGtCQUFrQjtRQUNsQixJQUFJLFVBQVUsRUFBRSxDQUFDO1lBQ2IsTUFBTSxVQUFVLEVBQUUsQ0FBQztRQUN2QixDQUFDO0lBQ0wsQ0FBQyxDQUFDO0lBRUYsT0FBTztRQUNILGFBQWE7UUFDYixlQUFlO1FBQ2YsYUFBYTtRQUNiLFNBQVM7UUFDVCxhQUFhO1FBQ2Isa0JBQWtCO1FBQ2xCLGdCQUFnQjtRQUNoQixvQkFBb0I7UUFDcEIsY0FBYztRQUNkLGdCQUFnQjtRQUNoQixjQUFjO1FBQ2QsZ0JBQWdCO1FBQ2hCLGNBQWM7UUFDZCxVQUFVO0tBQ2IsQ0FBQztBQUNOLENBQUMifQ==